import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import { Tour } from "antd";
import type { TourProps } from "antd";

import { AuthContext } from "./AuthContext";

import { editUserLoginApi } from "../services/User/UserApis";

export const TourContext = createContext<any>({});

type TourProviderProps = {
  children: ReactNode;
};

type TourContextType = {
  startTour: () => void;
  markPageAsVisited: (page: string) => void;
  refActiveGroup: React.RefObject<any>;
  refOptions: React.RefObject<any>;
  refNavigation: React.RefObject<any>;
  refAddMember: React.RefObject<any>;
};

export const TourProvider = ({ children }: TourProviderProps) => {
  const { currentUser } = useContext(AuthContext);

  const [open, setOpen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [pages, setPages] = useState<any>({});
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);
  const [currentUrl, setCurrentUrl] = useState(window.location.pathname);

  const navigate = useNavigate();

  const refActiveGroup = useRef<any>(null);
  const refOptions = useRef<any>(null);
  const refNavigation = useRef<any>(null);
  const refAddMember = useRef<any>(null);
  const refManageMember = useRef<any>(null);
  const refEditMember = useRef<any>(null);
  const refPaymentMethod = useRef<any>(null);
  const refGenerateDocument = useRef<any>(null);
  const refSendDocument = useRef<any>(null);
  const refLapsePolicy = useRef<any>(null);
  const refLoadPremium = useRef<any>(null);
  const refActivatePolicy = useRef<any>(null);
  const refAddGroup = useRef<any>(null);
  const refManageGroup = useRef<any>(null);
  const refEditGroup = useRef<any>(null);
  const refRemoveGroup = useRef<any>(null);
  const refDownloadStatement = useRef<any>(null);
  const refBulkPayment = useRef<any>(null);
  const refAddUser = useRef<any>(null);
  const refEditUser = useRef<any>(null);
  const refRemoveUser = useRef<any>(null);
  const refReports = useRef<any>(null);
  const refAddClaim = useRef<any>(null);
  const refManageClaim = useRef<any>(null);
  const refEditClaim = useRef<any>(null);

  const steps: TourProps["steps"] = [
    {
      title: "Current Group",
      description: "Displays the current Group you are working on.",
      target: () => refActiveGroup.current!,
      placement: "right",
    },
    {
      title: "Options",
      description: "Used to change current Group and Logging Out of the App.",
      target: () => refOptions.current!,
      placement: "left",
    },
    {
      title: "Side Menu",
      description: "Navigate through the application.",
      target: () => refNavigation.current!,
      placement: "right",
    },
    {
      title: "Add Member",
      description: "Add a new Member to the current Group.",
      target: () => refAddMember.current!,
      placement: "bottom",
    },
    {
      title: "Manage Member",
      description: "Manage the Member of the current Group.",
      target: () => refManageMember.current!,
      placement: "right",
    },
    {
      title: "Edit Member",
      description: "Edit the details of a Member.",
      target: () => refEditMember.current!,
      placement: "right",
    },
    {
      title: "Payment Method",
      description: "Manage the payment methods for the Member.",
      target: () => refPaymentMethod.current!,
      placement: "right",
    },
    {
      title: "Generate Document",
      description: "Generate Policy documents for the Member.",
      target: () => refGenerateDocument.current!,
      placement: "right",
    },
    {
      title: "Send Documents",
      description: "Send documents to the Member of the current Group.",
      target: () => refSendDocument.current!,
      placement: "right",
    },
    {
      title: "Lapse Policy",
      description: "Lapse a member's policy.",
      target: () => refLapsePolicy.current!,
      placement: "right",
    },
    {
      title: "Load Premium",
      description: "Load premium for a member.",
      target: () => refLoadPremium.current!,
      placement: "right",
    },
    {
      title: "Activate Policy",
      description: "Activate a lapsed policy.",
      target: () => refActivatePolicy.current!,
      placement: "right",
    },
    {
      title: "Add Group",
      description: "Add a new Group to the current Group.",
      target: () => refAddGroup.current!,
      placement: "bottom",
    },
    {
      title: "Manage Group",
      description: "Manage the Group.",
      target: () => refManageGroup.current!,
      placement: "right",
    },
    {
      title: "Download Statement",
      description: "Download a statement for the Group.",
      target: () => refDownloadStatement.current!,
      placement: "right",
    },
    {
      title: "Bulk Payment Allocation",
      description: "Allocate bulk payments for the Group.",
      target: () => refBulkPayment.current!,
      placement: "right",
    },
    {
      title: "Edit Group",
      description: "Edit the details of a Group.",
      target: () => refEditGroup.current!,
      placement: "right",
    },
    {
      title: "Remove Group",
      description: "Move the Group to inactive.",
      target: () => refRemoveGroup.current!,
      placement: "right",
    },
    {
      title: "Add User",
      description: "Add a new User to the current Group.",
      target: () => refAddUser.current!,
      placement: "bottom",
    },
    {
      title: "Edit User",
      description: "Edit the details of a User.",
      target: () => refEditUser.current!,
      placement: "right",
    },
    {
      title: "Remove User",
      description: "Move the User to inactive.",
      target: () => refRemoveUser.current!,
      placement: "right",
    },
    {
      title: "Available Reports",
      description: "List of all available Reports.",
      target: () => refReports.current!,
      placement: "bottom",
    },
    {
      title: "Add Claim",
      description: "Add a new Claim to the current Group.",
      target: () => refAddClaim.current!,
      placement: "bottom",
    },
    {
      title: "Manage Claim",
      description: "Manage the Claim.",
      target: () => refManageClaim.current!,
      placement: "right",
    },
    {
      title: "Edit Claim",
      description: "Edit the details of a Claim.",
      target: () => refEditClaim.current!,
      placement: "right",
    },
  ];

  const handleStart = () => {
    setPageLoaded(true);
    setOpen(true);
    setCurrentStep(0);
  };

  const markPageAsVisited = (page: string) => {
    setPages((prev: any) => {
      const updatedPages = { ...prev, [page]: true };
      localStorage.setItem("TourModal", JSON.stringify(updatedPages));
      return updatedPages;
    });
  };

  const handleStepChange = (current: number) => {
    setCurrentStep(current);

    if (current >= steps.length) {
      setOpen(false);
      return;
    }

    const step = steps[current];
    if (step) {
      const newUrl = getTargetUrl(step.title);

      if (newUrl && newUrl !== currentUrl) {
        setCurrentUrl(newUrl);
        navigate(newUrl);

        setPageLoaded(false);
        setOpen(false);

        setTimeout(() => {
          setPageLoaded(true);
          setOpen(true);
        }, 50);
      }
    }
  };

  const handleFinish = async () => {
    setCurrentStep((prev) => prev + 1);

    await editUserLoginApi(currentUser.id, {
      FirstLogin: false,
    });

    const updatedUser = { ...currentUser, FirstLogin: false };
    localStorage.setItem("CurrentUser", JSON.stringify(updatedUser));
  };

  const handleClose = async () => {
    setCurrentStep(steps.length);
    
    await editUserLoginApi(currentUser.id, {
      FirstLogin: false,
    });

    const updatedUser = { ...currentUser, FirstLogin: false };
    localStorage.setItem("CurrentUser", JSON.stringify(updatedUser));
  };

  const getTargetUrl = (title: any) => {
    switch (title) {
      case "Current Group":
        return "/dashboard";
      case "Options":
        return "/dashboard";
      case "Side Menu":
        return "/dashboard";
      case "Add Member":
        return "/members";
      case "Manage Member":
        return "/members";
      case "Edit Member":
        return "/members";
      case "Payment Method":
        return "/members";
      case "Generate Documents":
        return "/members";
      case "Send Documents":
        return "/members";
      case "Add Group":
        return "/groups";
      case "Manage Group":
        return "/groups";
      case "Edit Group":
        return "/groups";
      case "Remove Group":
        return "/groups";
      case "Add User":
        return "/users";
      case "Edit User":
        return "/users";
      case "Remove User":
        return "/users";
      case "Available Reports":
        return "/reports";
      case "Add Claim":
        return "/claims";
      case "Manage Claim":
        return "/claims";
      case "Edit Claim":
        return "/claims";
      default:
        return "";
    }
  };

  return (
    <TourContext.Provider
      value={{
        handleStart,
        handleFinish,
        handleClose,
        markPageAsVisited,
        setPageLoaded,
        setOpen,
        open,
        pageLoaded,
        pages,
        refActiveGroup,
        refOptions,
        refNavigation,
        refAddMember,
        refManageMember,
        refEditMember,
        refPaymentMethod,
        refGenerateDocument,
        refSendDocument,
        refLapsePolicy,
        refLoadPremium,
        refActivatePolicy,
        refAddGroup,
        refManageGroup,
        refEditGroup,
        refRemoveGroup,
        refDownloadStatement,
        refBulkPayment,
        refAddUser,
        refEditUser,
        refRemoveUser,
        refReports,
        refAddClaim,
        refManageClaim,
        refEditClaim,
      }}
    >
      {currentUser && currentUser.FirstLogin && (
        <Tour
          open={open}
          onClose={handleClose}
          onFinish={handleFinish}
          steps={steps}
          current={currentStep}
          onChange={handleStepChange}
          indicatorsRender={(current, total) => (
            <span>
              {current + 1} / {total}
            </span>
          )}
        />
      )}
      {children}
    </TourContext.Provider>
  );
};
